import React, { useEffect, useRef } from "react";
import { PspContext } from "@/config/contexts";
import { translate } from "@/config/translator";
import { Input, Label, Slot, Text, useId } from "@fluentui/react-components";
import { useContext } from "react";
import { PspInputProps } from "./pspTextInput.types";
import { useIsTablet } from "@/config/hooks";
import { clsx } from "clsx/lite";

export default function PspTextInput({
  label,
  labelString,
  showRequiredWarning = false,
  value,
  onChange,
  onKeyDown,
  type,
  onBlur,
  onFocus,
  placeholder,
  placeholderString,
  disabled,
  invalidDataMessage,
  whiteText,
  ref,
  contentBefore,
  disableAutocomplete,
  autoFocus,
  hideNumberInputArrows,
}: PspInputProps) {
  // -----------------------------------------------------------------------------
  // GENERAL
  // -----------------------------------------------------------------------------
  const { lang } = useContext(PspContext);
  const isTablet = useIsTablet();

  const localRef = useRef<HTMLInputElement>(null);

  const inputId = useId();

  function onChangeWrapper(event: React.ChangeEvent<HTMLInputElement>) {
    onChange(event.target.value);
  }

  useEffect(() => {
    if (autoFocus) {
      if (ref) {
        ref.current?.focus();
      } else if (localRef) {
        localRef.current?.focus();
      }
    }
  }, [autoFocus, ref]);

  return (
    <div className="flex flex-col w-full tablet:gap-2 group">
      <div className="flex flex-row gap-4 items-center">
        {/* LABEL */}
        <label
          className={clsx(
            "duration-200",
            "ease-in-out",
            whiteText
              ? "text-font-white"
              : clsx(
                  "text-font-secondary",
                  !disabled && "group-focus-within:text-font-black",
                ),
          )}
          htmlFor={inputId}
        >
          {label ? translate(label, lang) : labelString}
        </label>
        {showRequiredWarning && !value.trim() && (
          <Text
            size={200}
            className={whiteText ? "text-font-secondary" : "text-warning"}
          >
            {translate("fieldRequired", lang)}
          </Text>
        )}
        {invalidDataMessage && (
          <Text
            size={200}
            className={whiteText ? "text-font-secondary" : "text-warning"}
          >
            {translate(invalidDataMessage, lang)}
          </Text>
        )}
      </div>
      {/* INPUT */}
      {isTablet ? (
        <div className="w-full hidden tablet:block">
          <Input
            value={value}
            input={{
              className: hideNumberInputArrows
                ? [
                    "appearance-none",
                    "[-moz-appearance:textfield]",
                    "[&::-webkit-inner-spin-button]:appearance-none",
                    "[&::-webkit-outer-spin-button]:appearance-none",
                  ].join(" ")
                : "",
            }}
            className={clsx("w-full", whiteText && "text-font-white!")}
            placeholder={
              placeholder
                ? translate(placeholder, lang)
                : (placeholderString ?? "")
            }
            onChange={onChangeWrapper}
            autoFocus={autoFocus}
            onBlur={onBlur}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            disabled={disabled}
            autoComplete={disableAutocomplete ? "new-password" : undefined}
            type={type}
            size="large"
            ref={ref ?? localRef}
            required
            contentBefore={contentBefore as Slot<"span">}
            id={inputId}
          />
        </div>
      ) : (
        <div className="tablet:hidden w-full relative">
          <input
            type={type}
            value={value}
            onChange={onChangeWrapper}
            ref={ref ?? localRef}
            placeholder={
              placeholder
                ? translate(placeholder, lang)
                : (placeholderString ?? "")
            }
            className={clsx(
              "w-full",
              "py-1",
              "flex",
              "justify-start",
              "items-center",
              "flex-row",
              "border-b-neutral",
              "border-b",
              "outline-none",
              contentBefore && "pl-8",
              whiteText && "text-font-white!",
              hideNumberInputArrows &&
                `appearance-none
              [&::-webkit-inner-spin-button]:appearance-none
              [&::-webkit-outer-spin-button]:appearance-none
              [-moz-appearance:textfield]`,
            )}
          />
          {contentBefore && (
            <div className="absolute left-0 top-1/2 -translate-y-1/2">
              {contentBefore}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
